import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import {
  getCoverageTypes,
  useAuthenticatedMember,
} from '../../../../hooks/useCurrentMember/useCurrentMember';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { DateTime } from '../../../../utils/DateTime';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { getGuestPlanSelectionData } from '../../../GuestExperience/Guest.utils';
import {
  TimeStampContentContainer,
  TimeStampContentHeadingStyle,
  TimeStampContentSubTitleStyle,
  TimeStampDate,
} from './TimeStampContent.styled';

function getFormattedDate(): string {
  return DateTime.getCurrentDateTime().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

const COVERAGE_KEY_MAP: Record<string, string> = {
  M: '0',
  B: '1',
  V: '2',
  D: '3',
};

function matchesCoverage(item: any, coverage?: string): boolean {
  return String(item?.key) === COVERAGE_KEY_MAP[coverage?.toUpperCase() ?? ''];
}

function filterTimestampItems(
  items: any[],
  coverageContext: string | null,
  memberCoverages: string[]
): any[] {
  if (!items?.length) {
    return items;
  }

  if (!isEmpty(memberCoverages)) {
    return items.filter((item) =>
      memberCoverages.some((cov) => matchesCoverage(item, cov))
    );
  }

  if (coverageContext && COVERAGE_KEY_MAP[coverageContext.toUpperCase()]) {
    return items.filter((item) => matchesCoverage(item, coverageContext));
  }

  return items;
}

export const TimeStampContent = () => {
  const formattedDate = getFormattedDate();
  const data = useLagoon('landing-info')();
  const [enableTimestampCoverageDirectoryFilter] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIMESTAMP_COVERAGE_DIRECTORY_FILTER,
  ]);

  const currentMember = useAuthenticatedMember();
  const allMemberCoverages = getCoverageTypes(currentMember);

  const memberCoverages = enableTimestampCoverageDirectoryFilter
    ? allMemberCoverages
    : [];

  const timeStampLabel = find(data, {
    Timestamp_key: ConstantsLagoon.LANDING_INFO.TIME_STAMP_CONTENT,
  });

  const timeStampContent = data.filter(
    (item: any) => item.Timestamp_key !== 'TIME_STAMP_CONTENT'
  );

  const location = useLocation();
  let coverageType = null;
  if (enableTimestampCoverageDirectoryFilter) {
    if (location.pathname?.includes('print-directory')) {
      coverageType = getGuestPlanSelectionData()?.coverageTypeCode || '';
    } else {
      coverageType =
        new URLSearchParams(location.search).get('coverageType') || null;
    }
  }

  const filteredTimeStampContent = filterTimestampItems(
    timeStampContent,
    coverageType,
    memberCoverages
  );

  const isGEHAV2 = shouldUseGehaV2Styling();

  return (
    <React.Fragment>
      <TimeStampContentContainer cssProps={{ isGEHAV2 }}>
        <Text
          data-auto-testid="directory-update-timestamp"
          data-testid="directory-update-timestamp"
        >
          <TimeStampContentHeadingStyle cssProps={{ isGEHAV2 }}>
            {timeStampLabel?.TimeStamp_Header}
          </TimeStampContentHeadingStyle>
          <TimeStampContentSubTitleStyle cssProps={{ isGEHAV2 }}>
            {timeStampLabel?.Timestamp_Value}
          </TimeStampContentSubTitleStyle>
        </Text>
        <div
          className="date-info"
          data-auto-testid="directory-last-updated-container"
          data-testid="directory-last-updated-container"
        >
          {filteredTimeStampContent?.map((item, index) => (
            <TimeStampDate cssProps={{ isGEHAV2 }} key={index}>
              <span className="header">{item?.TimeStamp_Header}</span>{' '}
              {formattedDate}
            </TimeStampDate>
          ))}
        </div>
      </TimeStampContentContainer>
    </React.Fragment>
  );
};
